/**
 * Stations Hooks (Phase 9.2)
 *
 * TanStack Query hooks for station CRUD operations.
 * Stations represent physical equipment centers or work areas.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  StationWithCount,
  StationLookup,
  Station,
  CreateStation,
  UpdateStation,
} from '@guild/shared';

// Query keys
export const stationKeys = {
  all: ['stations'] as const,
  lists: () => [...stationKeys.all, 'list'] as const,
  list: (filters: { search?: string; departmentId?: string; includeInactive?: boolean }) =>
    [...stationKeys.lists(), filters] as const,
  lookup: () => [...stationKeys.all, 'lookup'] as const,
  details: () => [...stationKeys.all, 'detail'] as const,
  detail: (id: string) => [...stationKeys.details(), id] as const,
};

/**
 * Fetch stations list with camera/assessment counts
 */
export function useStations(options?: {
  search?: string;
  departmentId?: string;
  includeInactive?: boolean;
}) {
  const api = useApi();

  return useQuery({
    queryKey: stationKeys.list(options ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.search) params.set('search', options.search);
      if (options?.departmentId) params.set('departmentId', options.departmentId);
      if (options?.includeInactive) params.set('includeInactive', 'true');

      const queryString = params.toString();
      const endpoint = queryString ? `/api/stations?${queryString}` : '/api/stations';

      return api.get<StationWithCount[]>(endpoint);
    },
  });
}

/**
 * Fetch lightweight stations list for dropdowns
 */
export function useStationLookup() {
  const api = useApi();

  return useQuery({
    queryKey: stationKeys.lookup(),
    queryFn: () => api.get<StationLookup[]>('/api/stations/lookup'),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes (rarely changes)
  });
}

/**
 * Fetch single station
 */
export function useStation(id: string) {
  const api = useApi();

  return useQuery({
    queryKey: stationKeys.detail(id),
    queryFn: () => api.get<Station>(`/api/stations/${id}`),
    enabled: !!id,
  });
}

/**
 * Create station mutation
 */
export function useCreateStation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateStation) => api.post<Station>('/api/stations', data),
    onSuccess: () => {
      // Invalidate all station queries
      queryClient.invalidateQueries({ queryKey: stationKeys.all });
    },
  });
}

/**
 * Update station mutation
 */
export function useUpdateStation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStation }) =>
      api.put<Station>(`/api/stations/${id}`, data),
    onSuccess: (_, variables) => {
      // Invalidate list and specific detail
      queryClient.invalidateQueries({ queryKey: stationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: stationKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: stationKeys.lookup() });
    },
  });
}

/**
 * Delete station mutation (soft delete)
 */
export function useDeleteStation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ id: string }>(`/api/stations/${id}`),
    onSuccess: () => {
      // Invalidate all station queries
      queryClient.invalidateQueries({ queryKey: stationKeys.all });
    },
  });
}
